import {
  FormEvent, useState, useEffect, useCallback, useRef, CSSProperties,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Stack, TextField, DefaultButton, Icon,
} from '@fluentui/react';
import { Link } from 'react-router-dom';
import useDebounce from '../../components/hooks/useDebounce';
import { stackTokens15, textFieldStyles300 } from '../../utils';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import { toastError, toastSuccess } from '../../utils/toast';
import PageHeader from '../../components/parts/PageHeader';
import BillitInvoiceOverview from './components/BillitInvoiceOverview';
import { BillitInvoice } from './types';
import { billitColors } from './billitDetailUi';

type FilterStatus =
  | 'to-assign'
  | 'assigned'
  | 'approved'
  | 'rejected'
  | 'subject-to-approve'
  | 'subject-to-reject'
  | undefined;

const filterDotStyle: CSSProperties = {
  position: 'absolute',
  top: 4,
  right: 4,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: billitColors.purple,
};

const BillitInvoices = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>(undefined);
  const [invoices, setInvoices] = useState<BillitInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [subjectToApproveCount, setSubjectToApproveCount] = useState(0);
  const [subjectToRejectCount, setSubjectToRejectCount] = useState(0);
  const debouncedValue = useDebounce(filter, 500);
  const limit = 50;
  // Enkel het antwoord van de laatste opvraging mag de lijst vullen.
  const requestSeq = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const fetchFilterCount = useCallback(async (
    filterStatus: 'subject-to-approve' | 'subject-to-reject',
  ): Promise<number> => {
    const token = await getAccessTokenSilently();
    const params = new URLSearchParams({
      limit: '1',
      offset: '0',
      filter: filterStatus,
    });
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URI}/billit/invoices?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    if (!response.ok) return 0;
    const data = await response.json();
    return data.total ?? 0;
  }, [getAccessTokenSilently]);

  const refreshSubjectCounts = useCallback(async () => {
    try {
      const [approveCount, rejectCount] = await Promise.all([
        fetchFilterCount('subject-to-approve'),
        fetchFilterCount('subject-to-reject'),
      ]);
      setSubjectToApproveCount(approveCount);
      setSubjectToRejectCount(rejectCount);
    } catch {
      // Keep previous badge counts on transient errors
    }
  }, [fetchFilterCount]);

  const fetchInvoices = async (
    filterStatus?: FilterStatus,
    searchFilter = '',
    currentOffset = 0,
    append = false,
  ) => {
    const isInitial = currentOffset === 0 && !append;
    // Een trager antwoord van een vorige zoekterm (bv. de lijst zonder filter bij
    // het openen) overschreef anders de zoekresultaten. Vorige opvraging annuleren
    // en verouderde antwoorden negeren.
    requestSeq.current += 1;
    const requestId = requestSeq.current;
    const isStale = () => requestId !== requestSeq.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const token = await getAccessTokenSilently();

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
      });

      if (searchFilter) {
        params.append('search', searchFilter);
      }

      if (filterStatus) {
        params.append('filter', filterStatus);
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_BACKEND_URI
        }/billit/invoices?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        },
      );

      if (isStale()) return;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (isStale()) return;
      const newInvoices = data.invoices || [];
      const totalCount = data.total ?? 0;

      if (append) {
        setInvoices(prev => [...prev, ...newInvoices]);
      } else {
        setInvoices(newInvoices);
      }
      setTotal(totalCount);
      setOffset(currentOffset);
      setShowLoadMore(currentOffset + newInvoices.length < totalCount);
    } catch (error) {
      if (isStale() || (error instanceof DOMException && error.name === 'AbortError')) return;
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch invoices';
      dispatch(
        throwError({
          module: 'Billit Invoices Overview',
          message: errorMessage,
          level: SeverityLevel.Critical,
        }),
      );
      toastError(errorMessage);
    } finally {
      if (!isStale()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices(statusFilter, debouncedValue, 0, false);
      refreshSubjectCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, statusFilter, isAuthenticated]);

  const loadMoreCallback = () => {
    if (loading || loadingMore) return;
    const nextOffset = offset + limit;
    if (nextOffset >= total) return;
    fetchInvoices(statusFilter, debouncedValue, nextOffset, true);
  };

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue || '';
    setFilter(filterString);
  };

  const refetch = () => {
    fetchInvoices(statusFilter, debouncedValue, 0, false);
    refreshSubjectCounts();
  };

  // Manuele sync: documenten die de laatste dagen (vandaag inbegrepen) in Billit
  // gewijzigd zijn. De nachtelijke sync blijft bestaan.
  const [syncing, setSyncing] = useState(false);
  const syncWithBillit = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/sync/recent`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Synchroniseren mislukt (status ${response.status})`);
      }
      const r = data?.results || {};
      const errors = Array.isArray(r.errors) ? r.errors.length : 0;
      toastSuccess(
        `Billit gesynchroniseerd: ${r.created ?? 0} nieuw, ${r.updated ?? 0} bijgewerkt${
          errors ? `, ${errors} met fout` : ''
        }`,
      );
      refetch();
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Synchroniseren met Billit mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const handleReturnToToAssign = () => {
    if (statusFilter !== 'to-assign') {
      setStatusFilter('to-assign');
    } else {
      fetchInvoices('to-assign', debouncedValue, 0, false);
    }
    refreshSubjectCounts();
  };

  if (!isAuthenticated) return <p>Verboden</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title='Billit Facturen'
          iconName='Billit'
          loading={loading}
          actions={(
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type='button'
                onClick={syncWithBillit}
                disabled={syncing}
                title='Haal de documenten op die de laatste 3 dagen in Billit gewijzigd zijn (vandaag inbegrepen)'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#605e5c',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #edebe9',
                  background: '#faf9f8',
                  cursor: syncing ? 'default' : 'pointer',
                  opacity: syncing ? 0.7 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <Icon iconName='Sync' style={{ fontSize: '14px' }} />
                {syncing ? 'Bezig met synchroniseren…' : 'Synchroniseren met Billit'}
              </button>
              <Link
                to='/billit-invoices-sent'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#605e5c',
                  textDecoration: 'none',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #edebe9',
                  background: '#faf9f8',
                }}
              >
                <Icon iconName='Mail' style={{ fontSize: '14px' }} />
                Verstuurde facturen
              </Link>
            </div>
          )}
        />

        <TextField
          label='Zoek op factuurnummer of leverancier'
          placeholder='Factuurnummer of leverancier'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
        />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            marginTop: '15px',
            gap: '8px',
          }}
        >
          <DefaultButton
            text='Te behandelen'
            onClick={() => setStatusFilter('to-assign')}
            primary={statusFilter === 'to-assign'}
          />
          <DefaultButton
            text='Toegewezen'
            onClick={() => setStatusFilter('assigned')}
            primary={statusFilter === 'assigned'}
          />
          <DefaultButton
            text='Goedgekeurd'
            onClick={() => setStatusFilter('approved')}
            primary={statusFilter === 'approved'}
          />
          <DefaultButton
            text='Afgewezen'
            onClick={() => setStatusFilter('rejected')}
            primary={statusFilter === 'rejected'}
          />
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <DefaultButton
              text='Onderwerp goedkeuring'
              onClick={() => setStatusFilter('subject-to-approve')}
              primary={statusFilter === 'subject-to-approve'}
            />
            {subjectToApproveCount > 0 && (
              <span
                style={filterDotStyle}
                title={`${subjectToApproveCount} factuur/facturen`}
              />
            )}
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <DefaultButton
              text='Onderwerp afwijzing'
              onClick={() => setStatusFilter('subject-to-reject')}
              primary={statusFilter === 'subject-to-reject'}
            />
            {subjectToRejectCount > 0 && (
              <span
                style={filterDotStyle}
                title={`${subjectToRejectCount} factuur/facturen`}
              />
            )}
          </div>
          {statusFilter && (
            <DefaultButton
              text='Alle'
              onClick={() => setStatusFilter(undefined)}
            />
          )}
        </div>
      </div>

      <BillitInvoiceOverview
        invoices={invoices}
        loading={loading}
        loadingMore={loadingMore}
        refetch={refetch}
        total={total}
        showLoadMore={showLoadMore}
        loadMoreCallback={loadMoreCallback}
        onReturnToToAssign={handleReturnToToAssign}
      />
    </Stack>
  );
};

export default BillitInvoices;
